<template>
  <div class="gk-image-generator gk-ai-assistant">
    <LoadingAnimation v-if="generating" solid :animation="paintbrushAnimation" :animation-height="150" :display-text="$gettext('Generating image')" use-counter />
    <div class="prompt-wrap prompt">
      <div class="prompt-wrap__options">
        <b-form-group>
          <b-input-group>
            <b-form-select v-model="selectedDesignStyle" :options="designStyleOptions"></b-form-select>
          </b-input-group>
        </b-form-group>
        <b-form-group>
          <b-input-group>
            <b-form-select v-model="selectedColorPalette" :options="colorPaletteOptions"></b-form-select>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="prompt">
        <b-form-textarea
          v-model="requestMessage"
          @keydown="generateOnEnter"
          :placeholder="$gettext('Design a logo for an accounting company, showing a smiling and friendly lizard')"
          rows="5"
          no-resize
        />
        <div class="prompt__text-count" :class="{ maxfill: requestMessage.length >= maxPromptLength }">
          {{ requestMessage.length }}/{{ maxPromptLength }}
        </div>
      </div>
      <div style="display: flex; gap: 1rem; justify-content: space-between; width: 100%; padding-top: 1rem;">
        <GekkoButton 
          @click="$emit('cancel-generate', false)"
          :text="$gettext('Cancel')"
          variant="link"
          class="text-blue px-0"
        />
        <GekkoButton 
          @click="generateImage"
          :disabled="(loading || !requestMessage || requestMessage.length < 0)"
          :text="$gettext('Generate AI logo')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import GekkoButton from '@/components/GekkoButton'
import LoadingAnimation from "@/components/dashboard/ui/LoadingAnimation"

import * as paintbrushAnimation from '@/css/animations/paint.json'

import AiAssistantService from '@/store/services/ai.assistant.service.js'

export default {
  name: 'ImageGenerator',
  components: {
    GekkoButton,
    LoadingAnimation
  },
  props: {

  },
  data () {
    return {
      loading: false,
      generating: false,

      promptPlaceholder: this.$gettext('Design a logo for an accounting company, showing a green smiling and friendly lizard.'),
      designStyleOptions: [],
      colorPaletteOptions: [],
      requestMessage: '',
      selectedDesignStyle: null,
      selectedColorPalette: null,
      maxPromptLength: null,
      logoBotId: null,
      paintbrushAnimation: paintbrushAnimation.default,
      transparentBackground: true,
      requestLimitBasic: null,
      requestLimitPremium: null
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany'
    }),
    generateEnabled () {
      return this.requestMessage && this.requestMessage.length > 0
    }
  },
  created () {
    this.setCurrentStepStage('generator')
    this.setupData()
  },
  beforeDestroy () {
    this.setCurrentStepStage('')
  },
  methods: {
    ...mapMutations({
      setCurrentStepStage: 'wizard/SET_CURRENTSTEP_STAGE'
    }),
    setupData () {
      this.loading = true
      AiAssistantService.getCurrentLogoBot().then((response) => {
        this.maxPromptLength = response.data.max_prompt_length
        this.logoBotId = response.data.id
        this.requestLimitBasic = response.data.message_limit_basic
        this.requestLimitPremium = response.data.message_limit_premium
        AiAssistantService.getLogoStyleOptions().then((response) => {
          this.designStyleOptions = response.data.design_styles.filter(x => x.type === 'style').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })
          this.colorPaletteOptions = response.data.color_palettes.filter(x => x.type === 'palette').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })
          this.selectedDesignStyle = this.designStyleOptions[0].value
          this.selectedColorPalette = this.colorPaletteOptions[0].value
          this.loading = false
          this.$emit('loaded', { basic: this.requestLimitBasic, premium: this.requestLimitPremium } )
        })
      })
    },
    generateOnEnter (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.generateEnabled) {
          this.generateImage()
        }
      }
    },
    generateImage () {
      this.generating = true
      const logo_request = {
        bot_id: this.logoBotId,
        message: this.requestMessage,
        design_style: this.selectedDesignStyle,
        color_palette: this.selectedColorPalette,
        transparent_background: this.transparentBackground
      }
      if (window.mixpanel) {
        window.mixpanel.track('Wizard: logo - ai request', logo_request)
      }
      AiAssistantService.sendLogoRequest(logo_request).then((response) => {
        const logo_response = response.data
        this.$emit('generated', logo_response)
        if (window.mixpanel) {
          window.mixpanel.track('Wizard: logo - ai request - success')
        }
      }).catch((error) => {
        this.processAiErrorResponse(error)
      }).finally(() => {
        this.generating = false
      })
    },
    processAiErrorResponse (error) {
      let title, msg, reason
      switch (error.status) {
        case 403:
          reason = 'limit of free requests'
          // this.$store.commit('togglePremiumModal', 'web_ai_assistant')
          this.showPremiumConfirm()
          break;
        case 405:
          title = this.$gettext('Not allowed')
          msg = this.$gettext('You are not allowed to use this feature.')
          reason = 'not allowed'
          break;
        case 429:
          title = this.$gettext('Not allowed')
          msg = this.$gettext('You have already sent too many requests.')
          reason = 'too many messages'
          break;
        case 400:
          title = this.$gettext('Request rejected')
          msg = this.$gettext('Your prompt may contain text that is not allowed by our safety system.')
          reason = 'content policy violation'
          break;
        default:
          title = this.$gettext('Error')
          msg = this.$gettext('AI not responding.')
          reason = 'unspecified'
          break;
      }
      if (window.mixpanel) {
        window.mixpanel.track('Wizard: logo - error - ' + reason)
      }
      if (msg && msg !== '') {
        this.$store.commit('setAlert', { title: title, text: msg, variant: 'warning', duration: 4 })
      }
    },
    showPremiumConfirm () {

      let modal_message = this.$gettext('You ran out of tries to create your logo. You have to upgrade to Premium to keep using this feature. Otherwise, just pick one of your old logo from the gallery or decide to go without a logo for now.')
      let modal_confirm_text = this.$gettext('Upgrade now')
      
      if (this.currentCompany.is_premium) {
        modal_message = this.$gettext('You ran out of ai logo credits. If you need to use this feature more extensively please contact us. Otherwise, just pick one of your old logo from the gallery or decide to go without a logo for now.')
        modal_confirm_text = this.$gettext('Ok')
      }

      const ConfirmModalConstructor = this.$options.components['confirm-modal']
      const confirmModalInstance = new ConfirmModalConstructor({ propsData: {
        title: this.$gettext('Oef!'),
        message: modal_message,
        options: {
          variant: 'primary',
          confirmText: modal_confirm_text
        }
      }})
      confirmModalInstance.$mount()
      document.body.appendChild(confirmModalInstance.$el)

      confirmModalInstance.$on('confirmed', () => {
        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
          confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
        }
        if (!this.currentCompany.is_premium) {
          window.location.href = '/subscriptions/manage'
        }
      })

      confirmModalInstance.$on('cancel', () => {
        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
          confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
        }
      })
    }
  }
}
</script>

<style lang="scss">
  .gk-image-generator {
    height: var(--wiz-component-available-height);
   .prompt-wrap {
      border: 0;
      .form-group {
        margin-bottom: 0.5rem;
      }
      &__options {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        .form-group {
          display: flex;
          flex: 1;
          > div { width: 100%; }
        }
        .custom-select {
          border: 1px solid #B8B8BD;
          background: #FFFFFF url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3e%3cpath d='M239 401c9.4 9.4 24.6 9.4 33.9 0L465 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-175 175L81 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L239 401z'/%3e%3c/svg%3e") no-repeat right 15px center / 16px !important;
          padding: 0.375rem 2.5rem 0.375rem 0.75rem;
        }
      }
      .custom-select,
      .prompt {
        border-radius: $radiusLg;
        background-color: white;
      }
      .prompt {
        .send-message-btn:not(.disabled) {
          span {
            max-width: 100px;
          }
        }
        textarea {
          border-radius: $radiusLg;
          padding: 1rem;
          border: 1px solid #B8B8BD;
          background-color: white;
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  .gk-image-generator {

  }
</style>